import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { getToolContractAnnexes, updateToolContractAnnexes } from '../../../data/loca/tools';
import { notify, send } from '../../../services/NotificationService';

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});
const openAnnex = (document) => {
    if (!document) return;
    const payload = document.includes(',') ? document.split(',')[1] : document;
    const binary = atob(payload.replace(/\s/g, ''));
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export default function ContractAnnexesManager({ tool }) {
    const [annexes, setAnnexes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);
    const load = async () => {
        setLoading(true);
        try { setAnnexes((await getToolContractAnnexes(tool.publicId)) || []); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [tool.publicId]);
    const save = async (next) => {
        setSaving(true);
        try {
            const saved = await updateToolContractAnnexes(tool.publicId, next);
            setAnnexes(saved || next);
            send('success', 'Annexes du contrat enregistrées'); notify();
        } catch (error) { send('warning', error.message || 'Impossible d’enregistrer les annexes'); notify(); }
        finally { setSaving(false); }
    };
    const addFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        const invalid = files.find(file => file.type !== 'application/pdf' || file.size > MAX_FILE_SIZE);
        if (invalid) { send('warning', 'Chaque annexe doit être un PDF de 50 Mo maximum.'); notify(); return; }
        try {
            const additions = await Promise.all(files.map(async file => ({
                id: `${Date.now()}-${file.name}-${Math.random()}`,
                name: file.name.replace(/\.pdf$/i, ''),
                document: await readFile(file),
                includeInElectronicSignature: true
            })));
            await save([...annexes, ...additions]);
        } catch { send('warning', 'Impossible de lire une annexe.'); notify(); }
    };
    const update = (index, patch) => setAnnexes(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
    return <section className="tool-annexes-panel tool-details-card-full">
        <div className="tool-annexes-header"><div><h2>Annexes au contrat</h2><p>Les PDF sont ajoutés à la suite du contrat. Choisissez individuellement ceux qui doivent aussi faire partie du document SignNow.</p></div>
            <Button type="button" label="Ajouter des annexes" icon="pi pi-plus" onClick={() => inputRef.current?.click()} loading={saving} />
            <input ref={inputRef} type="file" accept="application/pdf" multiple hidden onChange={addFiles} />
        </div>
        {loading ? <p>Chargement…</p> : annexes.length === 0 ? <p className="tool-annexes-empty">Aucune annexe. Vous pouvez ajouter plusieurs documents PDF.</p> : <div className="tool-annexes-list">{annexes.map((annex, index) => <div className="tool-annexes-row" key={annex.id}>
            <i className="pi pi-file-pdf" aria-hidden="true" /><label><span>Nom de l’annexe</span><InputText value={annex.name} onChange={event => update(index, { name: event.target.value })} onBlur={() => save(annexes)} /></label>
            <label className="tool-annexes-signnow"><input type="checkbox" checked={annex.includeInElectronicSignature !== false} onChange={event => { const next = annexes.map((item, itemIndex) => itemIndex === index ? { ...item, includeInElectronicSignature: event.target.checked } : item); setAnnexes(next); save(next); }} /> <span>Inclure dans SignNow</span></label>
            <Button type="button" icon="pi pi-eye" rounded text aria-label="Visualiser l’annexe" onClick={() => openAnnex(annex.document)} />
            <Button type="button" icon="pi pi-trash" rounded text severity="danger" aria-label="Supprimer l’annexe" onClick={() => save(annexes.filter((_, itemIndex) => itemIndex !== index))} disabled={saving} />
        </div>)}</div>}
    </section>;
}
